// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorDashboardPage
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, ArrowRight, Package, ShoppingCart, Star, AlertCircle } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import { QuickStats } from '@/features/vendor/components/QuickStats';
import { VerificationStatus } from '@/features/vendor/components/VerificationStatus';
import { OrderStatusBadge } from '@/features/vendor/components/OrderStatusBadge';
import { useVendorDashboard } from '@/features/vendor/hooks/useVendorProfile';
import { useVendorPendingOrders } from '@/features/vendor/hooks/useVendorOrders';
import { useVendorPendingReviews } from '@/features/vendor/hooks/useVendorReviews';

export function VendorDashboardPage() {
  const { profile, stats, activity, isLoading } = useVendorDashboard();
  const { data: pendingOrders } = useVendorPendingOrders();
  const { data: pendingReviews } = useVendorPendingReviews();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen">
      <VendorHeader
        title={`Welcome back${profile ? `, ${profile.name}` : ''}`}
        subtitle="Here's what's happening with your store"
        actions={
          <Link
            to="/vendor/products/new"
            className="flex items-center gap-2 px-4 py-2 bg-terracotta text-white font-medium rounded-xl hover:bg-terracotta-light transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </Link>
        }
      />

      <div className="p-6 space-y-6">
        {/* Verification Status */}
        {profile && !profile.isVerified && (
          <VerificationStatus profile={profile} />
        )}

        {/* Quick Stats */}
        {stats && <QuickStats stats={stats} isLoading={isLoading} />}

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pending Orders */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-terracotta" />
                Pending Orders
              </h2>
              <Link
                to="/vendor/orders"
                className="text-sm font-medium text-terracotta hover:text-terracotta-light flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {pendingOrders?.items.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-warm-gray">No pending orders</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingOrders?.items.map((order) => (
                  <Link
                    key={order.id}
                    to={`/vendor/orders/${order.id}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-cream hover:bg-cream-dark transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-navy">{order.orderNumber}</p>
                        <p className="text-sm text-warm-gray">
                          {order.customer.contactName} • {order.productName}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-navy">
                        ${order.total.toLocaleString()}
                      </p>
                      <OrderStatusBadge status={order.status} size="sm" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Reviews Needing Response */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2">
                <Star className="w-5 h-5 text-terracotta" />
                Reviews
              </h2>
              <Link
                to="/vendor/reviews"
                className="text-sm font-medium text-terracotta hover:text-terracotta-light flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {pendingReviews?.items.length === 0 ? (
              <div className="text-center py-8">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-warm-gray">All reviews responded to!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingReviews?.items.slice(0, 3).map((review) => (
                  <div
                    key={review.id}
                    className="p-4 rounded-xl bg-cream"
                  >
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= review.rating
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-navy line-clamp-2 mb-2">
                      {review.content}
                    </p>
                    <p className="text-xs text-warm-gray">
                      {review.userName} • {review.productName}
                    </p>
                  </div>
                ))}
                {(pendingReviews?.items.length || 0) > 3 && (
                  <p className="text-sm text-center text-warm-gray">
                    +{(pendingReviews?.items.length || 0) - 3} more reviews need responses
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        {activity && activity.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-display text-lg font-semibold text-navy mb-6">
              Recent Activity
            </h2>
            <div className="space-y-4">
              {activity.map((item) => (
                <Link
                  key={item.id}
                  to={item.link}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-cream transition-colors"
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      item.type === 'order'
                        ? 'bg-blue-100 text-blue-600'
                        : item.type === 'review'
                        ? 'bg-amber-100 text-amber-600'
                        : item.type === 'inquiry'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-green-100 text-green-600'
                    }`}
                  >
                    {item.type === 'order' ? (
                      <ShoppingCart className="w-5 h-5" />
                    ) : item.type === 'review' ? (
                      <Star className="w-5 h-5" />
                    ) : (
                      <Package className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-navy">{item.title}</p>
                    <p className="text-sm text-warm-gray">{item.description}</p>
                  </div>
                  <span className="text-sm text-warm-gray">
                    {formatDate(item.timestamp)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorDashboardPage;
